import type { LDContext, LDReactProviderOptions } from "@launchdarkly/react-sdk";

import { useEffect, useState } from "react";

import {
	createLDReactProvider,
	useInitializationStatus,
	useLDClient,
} from "@launchdarkly/react-sdk";
import { captureException } from "@sentry/react";
import { FullScreenLoader } from "~/components/full-screen-loader";

import { useOrganization } from "@shared/auth";

const environmentContext = { key: import.meta.env.VITE_APP_ENV };

const context: LDContext = {
	environment: environmentContext,
	kind: "multi",
	organization: { anonymous: true, key: "anonymous" },
	user: { anonymous: true, key: "anonymous" },
};

const options: LDReactProviderOptions = {
	ldOptions: { streaming: true },
	startOptions: { timeout: 5 },
};

export const LDReactProvider = createLDReactProvider(
	import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID,
	context,
	options,
);

export function GuardLaunchDarkly({ children }: { children: React.ReactNode }) {
	const { status } = useInitializationStatus();
	const ldClient = useLDClient();
	const { id: organizationId, user } = useOrganization();
	const [isIdentified, setIsIdentified] = useState(false);

	useEffect(() => {
		if (status === "failed") {
			captureException(new Error("LaunchDarkly failed to initialize"));
		}
		if (status === "timeout") {
			captureException(new Error("LaunchDarkly initialization timed out"));
		}
		if (status === "complete" && !isIdentified) {
			void ldClient
				.identify({
					environment: environmentContext,
					kind: "multi",
					organization: { key: organizationId },
					user: { key: user?.sub ?? "" },
				})
				.finally(() => {
					setIsIdentified(true);
				});
		}
	}, [status, ldClient, user, organizationId, isIdentified]);

	if (status === "initializing" || (status === "complete" && !isIdentified)) {
		return <FullScreenLoader />;
	}
	return <>{children}</>;
}
